/*
 * Predictive search (header layer). Dehde 10x rewrite of the Urban <predictive-button> (bible surface "menu", notes 4-7).
 * Markup: snippets/search-bar.liquid (layer, states, map, person block) + sections/predictive-organizer.liquid (results).
 * Layer geometry, close button, backdrop, Tab trap and "any [data-dehde-search-field] opens the layer": assets/dehde-search.js.
 *
 * States on [data-dehde-search] data-state:
 *   idle     fewer than 2 characters → the site map (static, server-rendered)
 *   results  the organizer's listbox: "Brand e collezioni", "Pagine", "Prodotti" + "Vedi tutti i risultati"
 *   none     the organizer's [data-dehde-none] head + the same map + WhatsApp / preventivo
 * Kept from Urban: the 500ms debounce and the per-term cache.
 * Request: products, collections and pages, 4 EACH (limit_scope=each; with the shared limit products eat it).
 * Query suggestions and articles are no longer requested (the bible's typed groups replace them).
 * Keyboard (combobox pattern, focus stays in the field): ArrowDown/ArrowUp cycle every visible option and set
 * aria-selected + aria-activedescendant; Enter follows the active option, or with none active and 2+ characters
 * submits the form (→ /search?q=…&type=product&options[prefix]=last). Esc: Urban dropdown (closes, focus to opener).
 * Bold on the matched part: the bible's hl(), built with text nodes only (the query never goes through innerHTML).
 * Measurement: dataLayer select_item with the group name, only when a dataLayer exists.
 */
if (!customElements.get('predictive-button')) {
  customElements.define('predictive-button', class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cache = new Map();
      this.controller = null;
      this.active = -1;
      this.term = '';
    }

    connectedCallback() {
      if (this.dataset.predictive === 'false') return;
      this.form = this.closest('form');
      if (!this.form) return;
      this.input = this.form.querySelector('input[name="q"]');
      this.layer = this.form.querySelector('[data-dehde-search]');
      if (!this.input || !this.layer) return;
      this.target = this.layer.querySelector('[data-render-predict-results]');
      this.status = this.layer.querySelector('[data-dehde-status]');
      this.person = this.layer.querySelector('[data-dehde-person]');
      this.loader = this.form.querySelector('[data-loader]');

      this.debounced = debounce(() => this.refresh(), 500);
      this.input.addEventListener('input', () => {
        this.clearActive();
        this.debounced();
      });
      this.input.addEventListener('keydown', this.onKeydown.bind(this));
      this.target.addEventListener('click', this.onOptionClick.bind(this));
    }

    /* ---------- fetch + render ---------- */

    refresh() {
      const q = this.input.value.trim();
      this.term = q;
      if (q.length < 2) {
        if (this.controller) this.controller.abort();
        this.toggleLoader(false);
        this.target.replaceChildren();
        this.setState('idle');
        this.say('');
        return;
      }
      if (this.cache.has(q)) {
        this.render(q, this.cache.get(q));
        return;
      }
      this.fetchResults(q);
    }

    fetchResults(q) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.toggleLoader(true);
      const url = `${routes.predictive_search_url}?q=${encodeURIComponent(q)}&section_id=predictive-organizer`
        + '&resources[type]=product,collection,page&resources[limit]=4&resources[limit_scope]=each';
      fetch(url, { signal: this.controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const source = doc.querySelector('[data-render-predict-results]');
          if (!source) throw new Error('predictive-organizer: no results container');
          this.cache.set(q, source);
          if (q === this.input.value.trim()) this.render(q, source);
        })
        .catch((error) => {
          if (error && error.name === 'AbortError') return;
          console.error(error);
        })
        .finally(() => {
          if (q === this.input.value.trim()) this.toggleLoader(false);
        });
    }

    render(q, source) {
      this.toggleLoader(false);
      this.clearActive();
      // server-rendered section markup (terms escaped by Liquid): cloned, never built from the typed string
      this.target.replaceChildren(...Array.from(source.childNodes, (n) => document.importNode(n, true)));
      const found = this.target.querySelector('[data-dehde-found]');
      const none = this.target.querySelector('[data-dehde-none]');
      const words = PredictiveSearch.words(q);
      this.target.querySelectorAll('[data-dehde-hl]').forEach((el) => PredictiveSearch.highlight(el, words));
      this.updatePerson(q);

      if (found) {
        this.setState('results');
        const n = (k) => parseInt(found.dataset[k] || '0', 10);
        this.say(this.statusText(n('nProducts'), n('nCollections'), n('nPages')));
      } else {
        this.setState(none ? 'none' : 'idle');
        this.say(none ? this.layer.dataset.statusNone.replace('{q}', q) : '');
      }
    }

    setState(state) {
      this.layer.dataset.state = state;
      this.input.setAttribute('aria-expanded', state === 'results' ? 'true' : 'false');
      if (state !== 'results') this.input.removeAttribute('aria-activedescendant');
    }

    statusText(products, collections, pages) {
      const d = this.layer.dataset;
      const one = (n, single, many) => (n === 1 ? single : many.replace('{n}', n));
      return [
        one(products, d.statusProductOne, d.statusProductOther),
        d.statusCollections.replace('{n}', collections),
        one(pages, d.statusPageOne, d.statusPageOther)
      ].join(', ');
    }

    say(text) {
      if (this.status) this.status.textContent = text;
    }

    /* WhatsApp link of the no-results block: the typed query goes into ?text=, spaces as %20 (like dehde-wa-button) */
    updatePerson(q) {
      if (!this.person) return;
      const link = this.person.querySelector('a[href*="wa.me/"]');
      const template = this.person.dataset.waTemplate;
      if (!link || !template) return;
      const base = link.getAttribute('href').split('?')[0];
      link.setAttribute('href', `${base}?text=${encodeURIComponent(template.replace('{q}', q))}`);
    }

    toggleLoader(on) {
      if (this.loader) this.loader.classList.toggle('hidden', !on);
    }

    /* ---------- keyboard ---------- */

    options() {
      if (this.layer.dataset.state !== 'results') return [];
      return Array.from(this.target.querySelectorAll('[data-dehde-opt]'));
    }

    clearActive() {
      this.active = -1;
      this.target.querySelectorAll('[data-dehde-opt][aria-selected="true"]').forEach((o) => o.setAttribute('aria-selected', 'false'));
      this.input.removeAttribute('aria-activedescendant');
    }

    setActive(index) {
      const opts = this.options();
      if (!opts.length) return;
      this.active = (index + opts.length) % opts.length;
      opts.forEach((o, i) => o.setAttribute('aria-selected', i === this.active ? 'true' : 'false'));
      const current = opts[this.active];
      this.input.setAttribute('aria-activedescendant', current.id);
      (current.closest('.dehde-search_line') || current).scrollIntoView({ block: 'nearest' });
    }

    onKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp': {
          if (!this.options().length) return;
          event.preventDefault();
          const step = event.key === 'ArrowDown' ? 1 : -1;
          this.setActive(this.active < 0 ? (step > 0 ? 0 : -1) : this.active + step);
          break;
        }
        case 'Enter': {
          const opts = this.options();
          if (this.active > -1 && opts[this.active]) {
            event.preventDefault();
            PredictiveSearch.track(opts[this.active]);
            window.location.href = opts[this.active].href;
            return;
          }
          if (this.input.value.trim().length < 2) event.preventDefault();
          // otherwise the form submits: /search?q=…&type=product&options[prefix]=last
          break;
        }
        default:
      }
    }

    onOptionClick(event) {
      const option = event.target.closest('[data-dehde-opt]');
      if (option) PredictiveSearch.track(option);
    }

    static track(option) {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push({ event: 'select_item', item_list_name: option.dataset.dehdeGroup });
      }
    }

    /* ---------- matching + bold (bible hl): lowercase, accents stripped, "·" removed ---------- */

    static norm(s) {
      return String(s).toLowerCase().replace(/·/g, '').normalize('NFD').replace(/[̀-ͯ]/g, '');
    }

    static words(s) {
      return PredictiveSearch.norm(s).split(/[^a-z0-9]+/).filter(Boolean);
    }

    /* bold the first word-start match of any typed word; text nodes only */
    static highlight(el, qs) {
      const label = el.textContent;
      let ns = '';
      const at = [];
      for (let i = 0; i < label.length; i++) {
        const c = PredictiveSearch.norm(label.charAt(i));
        for (let k = 0; k < c.length; k++) { ns += c.charAt(k); at.push(i); }
      }
      let from = -1;
      let len = 0;
      for (let k = 0; k < qs.length && from < 0; k++) {
        let pos = ns.indexOf(qs[k]);
        while (pos > -1) {
          if (pos === 0 || !/[a-z0-9]/.test(ns.charAt(pos - 1))) { from = pos; len = qs[k].length; break; }
          pos = ns.indexOf(qs[k], pos + 1);
        }
      }
      if (from < 0) return;
      const a = at[from];
      const b = at[from + len - 1] + 1;
      const bold = document.createElement('b');
      bold.textContent = label.slice(a, b);
      el.replaceChildren(document.createTextNode(label.slice(0, a)), bold, document.createTextNode(label.slice(b)));
    }
  });
}

/* Urban global kept for reset-input-button (assets/global.js) on forms that still render one */
function toggleResults(flag, selector) {
  const resultsContainer = document.querySelector(selector);
  if (!resultsContainer) return;
  resultsContainer.classList.toggle('hidden', !flag);
}
